"use client";

import { CircleCheck as CheckCircle2, Loader as Loader2, Send } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function WorkOrderForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="relative bg-white border border-gray-300 rounded-lg p-8 md:p-12 shadow-lg">
        <div className="flex flex-col items-center text-center gap-6">
          <CheckCircle2 className="size-16 text-[#e63946]" strokeWidth={1.75} />
          <div className="font-sans text-xs tracking-[0.18em] uppercase text-gray-500">
            INQUIRY <span className="inline-block w-2 h-2 bg-[#e63946] mx-1 align-middle" /> RECEIVED
          </div>
          <h3 className="font-default font-bold text-2xl md:text-3xl uppercase tracking-tight text-black">
            Inquiry submitted.
          </h3>
          <p className="font-default text-base text-gray-600 max-w-md">{successMessage}</p>
          <Button onClick={resetForm} className="font-default font-semibold uppercase tracking-wider bg-[#e63946] text-white rounded-lg px-8 py-3 hover:bg-[#c62828]">
            Send Another
          </Button>
        </div>
      </div>;
  }
  return <div className="relative bg-white border border-gray-300 rounded-lg shadow-lg">
      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 md:px-6 py-3 gap-3 rounded-t-lg">
        <div className="font-sans text-[0.65rem] md:text-xs tracking-[0.18em] uppercase text-black truncate">
          PROWHIP <span className="inline-block w-1.5 h-1.5 bg-[#e63946] mx-2 align-middle" /> INQUIRY PORTAL
        </div>
      </div>

      <Form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="name" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans text-[0.7rem] tracking-[0.18em] uppercase text-gray-600">Name</FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" {...field} className="font-default border border-gray-300 rounded-md bg-gray-50 px-4 h-11 text-base focus:ring-2 focus:ring-[#e63946] focus:ring-opacity-50" />
                </FormControl>
                <FormMessage className="font-sans text-xs tracking-wider uppercase" />
              </FormItem>} />
          <FormField control={control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans text-[0.7rem] tracking-[0.18em] uppercase text-gray-600">Email</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="john@example.com" {...field} className="font-default border border-gray-300 rounded-md bg-gray-50 px-4 h-11 text-base focus:ring-2 focus:ring-[#e63946] focus:ring-opacity-50" />
                </FormControl>
                <FormMessage className="font-sans text-xs tracking-wider uppercase" />
              </FormItem>} />
        </div>

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-[0.7rem] tracking-[0.18em] uppercase text-gray-600">Phone (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="+44 07939 882235" {...field} className="font-default border border-gray-300 rounded-md bg-gray-50 px-4 h-11 text-base focus:ring-2 focus:ring-[#e63946] focus:ring-opacity-50" />
              </FormControl>
              <FormMessage className="font-sans text-xs tracking-wider uppercase" />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-[0.7rem] tracking-[0.18em] uppercase text-gray-600">Company (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="Your Business Name" {...field} className="font-default border border-gray-300 rounded-md bg-gray-50 px-4 h-11 text-base focus:ring-2 focus:ring-[#e63946] focus:ring-opacity-50" />
              </FormControl>
            </FormItem>} />

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-[0.7rem] tracking-[0.18em] uppercase text-gray-600">Message</FormLabel>
              <FormControl>
                <Textarea rows={6} placeholder="How can we help you?" {...field} className="font-default border border-gray-300 rounded-md bg-gray-50 px-4 py-2 text-base focus:ring-2 focus:ring-[#e63946] focus:ring-opacity-50 resize-none min-h-[140px]" />
              </FormControl>
              <FormMessage className="font-sans text-xs tracking-wider uppercase" />
            </FormItem>} />

        {submitError && <div className="border border-red-500 bg-red-50 p-4">
            <p className="font-sans text-xs tracking-wider uppercase text-red-600">{submitError}</p>
          </div>}

        <div className="flex justify-end pt-2">
          <Button type="submit" disabled={isSubmitting} className="font-default font-semibold uppercase tracking-[0.06em] text-sm bg-[#e63946] text-white rounded-lg px-8 py-6 h-auto hover:bg-[#c62828] transition-all">
            {isSubmitting ? <>
                <Loader2 className="animate-spin size-4 mr-2" />
                Sending...
              </> : <>
                Send Inquiry
                <Send className="size-4 ml-2" strokeWidth={2} />
              </>}
          </Button>
        </div>
      </Form>
    </div>;
}
export default function Inquiry() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="1642" id="professional-inquiry-portal" className="relative bg-white text-black py-24 md:py-36 overflow-hidden">
      <div className="relative max-w-[1280px] mx-auto px-4 sm:px-8">
        <div ref={headerRef} className={`max-w-[720px] mx-auto text-center mb-16 md:mb-20 transition-all duration-[520ms] ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="font-sans text-xs tracking-[0.18em] uppercase text-gray-500 mb-6 flex items-center justify-center gap-2">
            <span>Inquiry</span>
            <span className="inline-block w-2 h-2 bg-[#e63946]" />
            <span>Contact Us</span>
          </div>
          <h2 className="font-default font-bold uppercase tracking-tight leading-[0.94] text-black" style={{
          fontSize: "clamp(2.25rem, 5.4vw, 4.75rem)"
        }}>
            Get in touch with <span className="text-[#e63946]">ProWhip</span>
          </h2>
          <p className="font-default text-lg text-gray-600 mt-6 max-w-xl mx-auto">
            Have questions about our premium cream chargers? Fill out the form below and our team will get back to you shortly.
          </p>
        </div>

        <div ref={formRef} className={`max-w-3xl mx-auto transition-all duration-[520ms] ease-out delay-150 ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <FormProvider formKey="professional_inquiry_form_inquiry" formId="professional_inquiry_form_inquiry" sectionName="professional-inquiry-portal" defaultValues={{
          name: "",
          email: "contact@prowhip.one",
          phone: "+44 07939 882235",
          company: "",
          message: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: false
          },
          message: {
            required: true
          }
        }} submitText="Send Inquiry" successMessage="Thank you for your inquiry. We will respond within 24 hours." errorMessage="Transmission failed. Please try again.">
            <WorkOrderForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}